import { effectiveEnergy, type Card, type CardEvent, type EventData, type EventKind } from '@hmawiw/shared';
import { create } from 'zustand';
import { getDb } from '../db/handle';
import { deleteEvents, readEvents, writeEvents, type Db, type EventEntry } from '../db/idb';
import { newId } from '../lib/ids';
import { useSettings } from './settings';

interface EventsState {
  hydrated: boolean;
  /** Every event this device knows, oldest first. A personal board: thousands at most. */
  events: EventEntry[];
  hydrate(db: Db): Promise<void>;
  /** Records something that happened here; pushed at the next sync. */
  log(kind: EventKind, cardId: string | null, data: EventData): Promise<void>;
  pendingEvents(): CardEvent[];
  markSent(ids: string[]): Promise<void>;
  /** Server data enters only here; it never marks anything pending. */
  applyRemote(events: CardEvent[]): Promise<void>;
  requeueAll(): Promise<void>;
  /** After a purge resync: events of cards the server no longer has, unless still to be pushed. */
  dropForCards(cardIds: string[]): Promise<void>;
  reset(): void;
}

function byAt(a: EventEntry, b: EventEntry): number {
  return a.at - b.at || a.id.localeCompare(b.id);
}

/** The state most events carry: where the card sits and how the user's energy stands. */
export function snapshotOf(card: Pick<Card, 'motivation' | 'band' | 'energy'>, now: number): EventData {
  return { motivation: card.motivation, band: card.band, energy: card.energy, myEnergy: effectiveEnergy(useSettings.getState().energyNow, now) ?? 0 };
}

export const useEvents = create<EventsState>((set, get) => ({
  hydrated: false,
  events: [],

  async hydrate(db) {
    const events = (await readEvents(db)).sort(byAt);
    set({ hydrated: true, events });
  },

  async log(kind, cardId, data) {
    const entry: EventEntry = { id: newId(), cardId, at: Date.now(), kind, data, pending: 1 };
    await writeEvents(getDb(), [entry]);
    set((s) => ({ events: [...s.events, entry] }));
  },

  pendingEvents() {
    return get()
      .events.filter((e) => e.pending === 1)
      .map(({ pending: _p, ...e }) => e);
  },

  async markSent(ids) {
    const done = new Set(ids);
    const updated = get().events.map((e) => (done.has(e.id) && e.pending === 1 ? { ...e, pending: 0 as const } : e));
    await writeEvents(getDb(), updated.filter((e) => done.has(e.id)));
    set({ events: updated });
  },

  async applyRemote(remote) {
    if (remote.length === 0) return;
    const known = new Map(get().events.map((e) => [e.id, e]));
    const fresh: EventEntry[] = [];
    for (const r of remote) {
      // An entry this device wrote stays pending until the push is acknowledged, even if the pull shows it first.
      if (known.has(r.id)) continue;
      fresh.push({ ...r, pending: 0 });
    }
    if (fresh.length === 0) return;
    await writeEvents(getDb(), fresh);
    for (const f of fresh) known.set(f.id, f);
    set({ events: [...known.values()].sort(byAt) });
  },

  async requeueAll() {
    const events = get().events.map((e) => ({ ...e, pending: 1 as const }));
    await writeEvents(getDb(), events);
    set({ events });
  },

  async dropForCards(cardIds) {
    const gone = new Set(cardIds);
    const drop = get().events.filter((e) => e.cardId !== null && gone.has(e.cardId) && e.pending === 0);
    if (drop.length === 0) return;
    await deleteEvents(getDb(), drop.map((e) => e.id));
    const ids = new Set(drop.map((e) => e.id));
    set((s) => ({ events: s.events.filter((e) => !ids.has(e.id)) }));
  },

  reset() {
    set({ hydrated: false, events: [] });
  },
}));
